import { NgbDate } from '@ng-bootstrap/ng-bootstrap';
import { IcuPatientComponent } from '../routes/screen/icu-patient/icu-patient.component';

export function getYearOfDate(date: string): number {
  return +date.slice(0, 4);
}

export function getMonthOfDate(date: string): number {
  return +date.slice(5, 7);
}

export function getDayOfDate(date: string): number {
  return +date.slice(8, 10);
}

export function ngbToDbDate(date: NgbDate): string {
  return date.year + '-' + date.month + '-' + date.day;
}

export function dbToNgbDate(date: string): NgbDate | null {
  return date?.length > 9 ? new NgbDate(getYearOfDate(date), getMonthOfDate(date), getDayOfDate(date)) : null;
}

export function calculateAge(birthday) { // birthday is a date
  const today = new Date();
  const birthDate = new Date(birthday);
  let age = today.getFullYear() - birthDate.getFullYear();
  const m = today.getMonth() - birthDate.getMonth();
  if (m < 0 || (m === 0 && today.getDate() < birthDate.getDate())) {
    age = age - 1;
  }

  return age;
}

export function getColorByLevel(level: number): string {
  const colors = ['green', 'yellow', 'orange', 'red'];
  return colors[level];
}

export function getPriorityByLevel(level: number): string {
  const priority = ['', 'low priority', 'middle priority', 'high priority'];
  return priority[level];
}

export function getFollowUpActions(): any {
  return [
    { action: -2, actionName: '' },
    { action: 0, actionName: 'Continue routine observation' },
    { action: 1, actionName: 'Urgent assessment from the ward doctor' },
    { action: 2, actionName: 'Emergent assessment from the ward doctor' },
    { action: 3, actionName: 'Emergent assessment  by clinical team' },
    { action: -1, actionName: 'Reject' },
  ];
}

export function getPosition(pos, translate) {
  switch (pos) {
  case 0:
    return translate.instant('Upside down'); //562=Лежащ по очи
  case 1:
    return translate.instant('Lay'); //559=Лежащ
  case 2:
    return translate.instant('Lay left'); //563=Лежащ наляво
  case 3:
    return translate.instant('Lay right'); //564=Лежащ надясно
  case 4:
    return translate.instant('Sitting'); //553 седнал
  case 5:
    return translate.instant('Lean back'); //561=Облегнат
  case 6:
    return translate.instant('Standing'); //554=Стоящ
  case 7:
    return translate.instant('Walking'); //555=Вървящ
  case 8:
    return translate.instant('Running'); // 556=Бягащ
  case 10:
    return translate.instant('Prone'); // 556=Бягащ

  default:
    return translate.instant('undefined');
  }
}

function isBitZero(sigstat: number, bit: number): boolean {
  // JS bitwise operators work on 32-bit ints, use Math for bits >= 32
  if (bit >= 32) {
    return (Math.floor(sigstat / Math.pow(2, bit)) % 2) === 0;
  }
  return (sigstat & (1 << bit)) === 0;
}

export function getDetachedSensors(sigstat: number) {
  // Detached sensors use bits 0-15 (bit=0 means detached)
  // Bit 32: STETH attached (bit=1 means attached)
  const sensorMap = [
    { name: 'I', bit: 0 },      // e0
    { name: 'II', bit: 1 },     // e1
    { name: 'Dorsalis', bit: 1 }, // Same bit as 'II' (e1)
    { name: 'V2', bit: 2 },     // e2
    { name: 'V3', bit: 3 },     // e3
    { name: 'V4', bit: 4 },     // e4
    { name: 'V5', bit: 5 },     // e5
    { name: 'V6', bit: 6 },     // e6
    { name: 'V1', bit: 7 },     // e7
    { name: 'III', bit: 8 },
    { name: 'Anterior', bit: 9 },
    { name: 'Inferior', bit: 10 },
    { name: 'aVR', bit: 11 },
    { name: 'aVL', bit: 12 },
    { name: 'aVF', bit: 13 },
    { name: 'PPG', bit: 14 },
    { name: 'RESP', bit: 15 },
    { name: 'STETH', bit: 32 },
  ];

  return sensorMap
    .filter(sensor => isBitZero(sigstat, sensor.bit))
    .map(sensor => sensor.name);
}

export function getNoisySensors(sigstat: number) {
  // Noisy sensors use bits 16-31 (bit=0 means noisy)
  // Bit 33: STETH clean (bit=1 means clean)
  const sensorMap = [
    { name: 'I', bit: 16 },      // e0
    { name: 'II', bit: 17 },     // e1
    { name: 'Dorsalis', bit: 17 }, // Same bit as 'II' (e1)
    { name: 'V2', bit: 18 },     // e2
    { name: 'V3', bit: 19 },     // e3
    { name: 'V4', bit: 20 },     // e4
    { name: 'V5', bit: 21 },     // e5
    { name: 'V6', bit: 22 },     // e6
    { name: 'V1', bit: 23 },     // e7
    { name: 'III', bit: 24 },
    { name: 'Anterior', bit: 25 },
    { name: 'Inferior', bit: 26 },
    { name: 'aVR', bit: 27 },
    { name: 'aVL', bit: 28 },
    { name: 'aVF', bit: 29 },
    { name: 'PPG', bit: 30 },
    { name: 'RESP', bit: 31 },
    { name: 'STETH', bit: 33 },
  ];

  return sensorMap
    .filter(sensor => isBitZero(sigstat, sensor.bit))
    .map(sensor => sensor.name);
}

const channelSensorMap = {
  1: ['I', 'PPG', 'RESP', 'STETH'], // 1 channel
  2: ['I', 'II', 'Dorsalis', 'III', 'Anterior', 'Inferior', 'aVR', 'aVL', 'aVF', 'PPG', 'RESP', 'STETH'], // 3 channel
  3: ['I', 'II', 'III', 'aVR', 'aVL', 'aVF', 'V1', 'V2', 'V3', 'V4', 'V5', 'V6', 'PPG', 'RESP', 'STETH'], // 12 channel
  4: ['I', 'II', 'aVR', 'aVL', 'aVF', 'PPG', 'RESP', 'STETH'], // 2 channel
};

export function getDetachedSensorsByChannel(sigstat: number, channel: number) {
  const detached = getDetachedSensors(sigstat);

  if (detached.length === 0) {
    return [];
  }

  const allowedSensors = channelSensorMap[channel] || [];
  return detached.filter(sensor => allowedSensors.includes(sensor));
}

export function getNoisySensorsByChannel(sigstat: number, channel: number) {
  const noisy = getNoisySensors(sigstat);

  if (noisy.length === 0) {
    return [];
  }

  const allowedSensors = channelSensorMap[channel] || [];
  return noisy.filter(sensor => allowedSensors.includes(sensor));
}


export function mapPosition(pos) {
  const map = {
    1: 4,//553 седнал
    2: 6,//554=Стоящ
    3: 7,//555=Вървящ
    4: 8,// 556=Бягащ
    6: 8,//557=Шофиращ
    7: 1,//559=Лежащ
    8: 2,//563=Лежащ наляво
    9: 3,//564=Лежащ надясно
    10: 0,//562=Лежащ по очи
    11: 5 //561=Облегнат
  };
  return map.hasOwnProperty(pos) ? map[pos]: null;
}

export function mapActivity(pos) {
  const map = {
    1: 'low',//553 седнал
    2: 'medium',//554=Стоящ
    3: 'high',//555=Вървящ
    4: 'high',// 556=Бягащ
    6: 'high',//557=Шофиращ
    7: 'low',//559=Лежащ
    8: 'low',//563=Лежащ наляво
    9: 'low',//564=Лежащ надясно
    10: 'low',//562=Лежащ по очи
    11: 'medium' //561=Облегнат
  };
  return map.hasOwnProperty(pos) ? map[pos]: 'undefined';
}

export function playSound(level: number) {
  const audio = new Audio();
  switch (level) {
  case 2:
    audio.src = '/assets/sounds/beep-08.mp3';
    audio.load();
    audio.play();
    break;
  case 3:
  default:
    audio.src = '/assets/sounds/beep-09.mp3';
    audio.load();
    audio.play();
    break;
  }
}

export function getUniqueID() {
  return Math.random().toString(36).substr(2, 9);
}
export function formatDateTimeForApi(date) {
  return (date == null) ? null : new Date(date).toISOString().replace('T', ' ').substr(0, 19);
}
export function formatTimeForApi(date) {
  return (date == null) ? null : new Date(date).toTimeString().substr(0, 5);
}
export function formatDateForApi(date) {
  const dt = new Date(date);
  return (date == null) ? null : `${dt.getFullYear()}-${('0' + (dt.getMonth() + 1)).slice(-2)}-${('0' + dt.getDate()).slice(-2)}`;
}
export function calculateBMI(weight: any, height: any) {
  return (weight / (Math.pow((height / 100), 2))).toFixed(2);
}
export function calculateBSI(weight: any, height: any) {
  return (Math.sqrt(height * weight / 3600)).toFixed(2);
}
export function isNumberKey(evt, control) {
  const charCode = (evt.which) ? evt.which : evt.keyCode;
  //access "+" at start 
  if (charCode == 43 && this.form.value.basic_info[control].length == 0) {
    return true;
  }
  if (charCode != 46 && charCode > 32
        && (charCode < 48 || charCode > 57))
    return false;

  return true;
}

export function getVal(value: any, decimalPlaces: number) {
  if (value == null) return 'N/A';
  return +parseFloat('' + value).toFixed(decimalPlaces);
}
export function getValBlack(value: any, decimalPlaces: number) {
  if (value === null) return value;
  return +parseFloat('' + value).toFixed(decimalPlaces);
}

export function splitRRule(rRuleString: string) {
  //FREQ=WEEKLY;COUNT=5;INTERVAL=1;WKST=MO;BYDAY=MO,WE,TH,FR
  const data = [];
  (rRuleString.split(';')).forEach(element => {
    const elData = element.split('=');
    data[elData[0]] = elData[1];
  });
  return data;
}

export function getEventTime(string) {
  console.log('getEventTime', string);
  const time = string.split(':');
  var now = new Date();
  now.setHours(time[0]);
  now.setMinutes(time[1]);
  now.setMilliseconds(0);
  console.log('getEventTime', now);
  return now;
}

export enum MapVitalParamsColorsBlackScreen {
  'o2' = '#02FFFF',
  'br' = '#FFFF00',
  'hr' = '#02FF33',
  'tp' = '#FFFFFF',
  'ph' = '#FF0000',
}

export function  pinchZoom(imageElement) {
  let imageElementScale = 1;

  const start = {} as any;

  // Calculate distance between two fingers
  const distance = (event) => {
    return Math.hypot(event.touches[0].pageX - event.touches[1].pageX, event.touches[0].pageY - event.touches[1].pageY);
  };

  imageElement.addEventListener('touchstart', (event) => {
    if (event.touches.length === 2 && !start.x) {
      event.preventDefault(); // Prevent page scroll

      // Calculate where the fingers have started on the X and Y axis
      start.x = (event.touches[0].pageX + event.touches[1].pageX) / 2;
      start.y = (event.touches[0].pageY + event.touches[1].pageY) / 2;
      start.distance = distance(event);
    }
  });

  imageElement.addEventListener('touchmove', (event) => {
    if (event.touches.length === 2) {
      event.preventDefault(); // Prevent page scroll
      let scale;

      // Safari provides event.scale as two fingers move on the screen
      // For other browsers just calculate the scale manually
      if (event.scale) {
        scale = event.scale;
      } else {
        const deltaDistance = distance(event);
        scale = deltaDistance / start.distance;
      }

      imageElementScale = Math.min(Math.max(1, scale), 4);

      // Calculate how much the fingers have moved on the X and Y axis
      const deltaX = (((event.touches[0].pageX + event.touches[1].pageX) / 2) - start.x) * 2; // x2 for accelarated movement
      const deltaY = (((event.touches[0].pageY + event.touches[1].pageY) / 2) - start.y) * 2; // x2 for accelarated movement

      // Transform the image to make it grow and move with fingers
      const transform = `translate3d(${deltaX}px, ${deltaY}px, 0) scale(${imageElementScale})`;
      imageElement.style.transform = transform;
      imageElement.style.WebkitTransform = transform;
      imageElement.style.zIndex = '9999';
    }
  });
  // imageElement.addEventListener('touchend', (event) => {
  //   console.log('touchend', event);
  //   // Reset image to it's original format
  //   imageElement.style.transform = "";
  //   imageElement.style.WebkitTransform = "";
  //   imageElement.style.zIndex = "";
  // });
}


export function isIntegerString(str) {
  return /^-?\d+$/.test(str);
}
